const API_BASE   = '/api';
const TOKEN_KEY  = 'gol_token';

// ── Token helpers ──────────────────────────────────────────────────────────
export const getToken  = () => localStorage.getItem(TOKEN_KEY);
export const setToken  = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem('gol_active_player_id');
};

async function api(path, options = {}) {
  const config = {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  };

  const token = getToken();
  if (token) config.headers['Authorization'] = `Bearer ${token}`;

  if (config.body && typeof config.body === 'object' && !(config.body instanceof FormData)) {
    config.body = JSON.stringify(config.body);
  }
  if (config.body instanceof FormData) delete config.headers['Content-Type'];

  const res = await fetch(`${API_BASE}${path}`, config);

  if (res.status === 401) {
    clearAuth();
    window.location.reload();
    return;
  }

  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(err.error || 'Request failed');
  }
  return res.json();
}

// ── Auth ───────────────────────────────────────────────────────────────────
export const auth = {
  register: (data) => fetch(`${API_BASE}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }).then(async r => {
    const body = await r.json();
    if (!r.ok) throw new Error(body.error || 'Request failed');
    return body;
  }),
  login: (data) => fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  }).then(async r => {
    const body = await r.json();
    if (!r.ok) throw new Error(body.error || 'Request failed');
    return body;
  }),
  me:     () => api('/auth/me'),
  logout: () => { clearAuth(); window.location.reload(); },
};

// ── Players ────────────────────────────────────────────────────────────────
export const players = {
  list:   ()         => api('/players'),
  me:     ()         => api('/players/me'),
  update: (id, data) => api(`/players/${id}`, { method: 'PUT',    body: data }),
  delete: (id)       => api(`/players/${id}`, { method: 'DELETE' }),
};

// ── Quests ─────────────────────────────────────────────────────────────────
export const quests = {
  list: (_playerId, params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return api(`/quests${qs ? '?' + qs : ''}`);
  },
  get:      (id)       => api(`/quests/${id}`),
  chain:    (id)       => api(`/quests/${id}/chain`),
  create:   (data)     => api('/quests',               { method: 'POST',  body: data }),
  update:   (id, data) => api(`/quests/${id}`,         { method: 'PUT',   body: data }),
  complete: (id)       => api(`/quests/${id}/complete`, { method: 'PATCH' }),
  delete:   (id)       => api(`/quests/${id}`,         { method: 'DELETE' }),
};

// ── Food ───────────────────────────────────────────────────────────────────
export const food = {
  search: (query) => api(`/food/search?query=${encodeURIComponent(query)}`),

  // tz_off is passed so the backend filters by local date, not UTC date
  list: (_playerId, date, tz_off = 0) => {
    const params = new URLSearchParams();
    if (date)   params.set('date',      date);
    if (tz_off) params.set('tz_offset', tz_off);
    const qs = params.toString();
    return api(`/food${qs ? '?' + qs : ''}`);
  },

  totals: (_playerId, date, tz_off = 0) => {
    const params = new URLSearchParams();
    if (date)   params.set('date',      date);
    if (tz_off) params.set('tz_offset', tz_off);
    const qs = params.toString();
    return api(`/food/totals${qs ? '?' + qs : ''}`);
  },

  create: (data)     => api('/food',       { method: 'POST', body: data }),
  update: (id, data) => api(`/food/${id}`, { method: 'PUT',  body: data }),
  delete: (id)       => api(`/food/${id}`, { method: 'DELETE' }),
};

// ── Shop ───────────────────────────────────────────────────────────────────
export const shop = {
  list:        ()         => api('/shop'),
  get:         (id)       => api(`/shop/${id}`),
  create:      (data)     => api('/shop',            { method: 'POST', body: data }),
  update:      (id, data) => api(`/shop/${id}`,      { method: 'PUT',  body: data }),
  delete:      (id)       => api(`/shop/${id}`,      { method: 'DELETE' }),
  redeem:      (data)     => api('/shop/redeem',     { method: 'POST', body: data }),
  redemptions: ()         => api('/shop/redemptions'),
};

// ── Budget ─────────────────────────────────────────────────────────────────
export const budget = {
  list: (_playerId, month, year) => {
    const qs = month && year ? `?month=${month}&year=${year}` : '';
    return api(`/budget${qs}`);
  },
  total: (_playerId, month, year) => {
    const qs = month && year ? `?month=${month}&year=${year}` : '';
    return api(`/budget/total${qs}`);
  },
  create: (data)     => api('/budget',       { method: 'POST', body: data }),
  update: (id, data) => api(`/budget/${id}`, { method: 'PUT',  body: data }),
  delete: (id)       => api(`/budget/${id}`, { method: 'DELETE' }),
};

// ── Stats ──────────────────────────────────────────────────────────────────
export const stats = {
  get:         (_playerId, tz_off = 0) => api(`/stats/me${tz_off ? '?tz_offset=' + tz_off : ''}`),
  upcoming:    (_playerId, days = 7)   => api(`/stats/upcoming?days=${days}`),
  activity:    (_playerId, limit = 8)  => api(`/stats/activity?limit=${limit}`),
  leaderboard: ()                      => api('/stats/leaderboard'),
};

// ── Custom Foods ───────────────────────────────────────────────────────────
export const customFoods = {
  list:   (search)     => {
    const qs = search?.trim() ? `?search=${encodeURIComponent(search.trim())}` : '';
    return api(`/custom-foods${qs}`);
  },
  create: (data)       => api('/custom-foods',       { method: 'POST', body: data }),
  update: (id, data)   => api(`/custom-foods/${id}`, { method: 'PUT',  body: data }),
  delete: (id)         => api(`/custom-foods/${id}`, { method: 'DELETE' }),
};

// ── Uploads ────────────────────────────────────────────────────────────────
export const uploads = {
  upload: (file) => {
    const fd = new FormData();
    fd.append('image', file);
    return api('/uploads', { method: 'POST', body: fd });
  },
};